// src/modules/competitions/pages/MyCompetitions.tsx - CORREGIDO

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import CompetitionCard from '../components/CompetitionCard';
import ParticipantStatus from '../components/ParticipantStatus';
import ImageUpload from '../components/ImageUpload';
import {
  Competition,
  ParticipantStatus as ParticipantStatusType,
  StatusResponse,
  CompetitionImageType,
  CompetitionSubmission,
  ImageUploadData,
  CompetitionDetails as CompetitionDetailsType
} from '../types';
import { competitionService } from '../services/competitionService';
import { useAppContext } from '../../../shared/context/AppContext';
import Button from '../../../shared/components/ui/Button';
import '../styles/participantStatus.css';
type TabType = 'all' | 'active' | 'completed';

const MyCompetitions: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { auth } = state;

  const [participations, setParticipations] = useState<ParticipantStatusType[]>([]);
  const [competitions, setCompetitions] = useState<CompetitionDetailsType[]>([]);
  const [selectedCompetition, setSelectedCompetition] = useState<number | null>(null);
  const [competitionStatus, setCompetitionStatus] = useState<StatusResponse | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.isAuthenticated && auth.token) {
      loadMyCompetitions();
    } else {
      navigate('/login');
    }
  }, [auth.isAuthenticated, auth.token]);

  useEffect(() => {
    if (selectedCompetition && auth.token) {
      loadCompetitionStatus(selectedCompetition);
    }
  }, [selectedCompetition]);

  const loadMyCompetitions = async () => {
    if (!auth.token) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await competitionService.getCompetitions();

      if (response.success && response.data) {
        setCompetitions(response.data.competitions);

        const statusPromises = response.data.competitions.map(async (compDetails) => {
          try {
            const statusResponse = await competitionService.getParticipantStatus(compDetails.competition.id);
            return statusResponse.success ? statusResponse.data?.participant_status : null;
          } catch {
            return null;
          }
        });

        const statuses = await Promise.all(statusPromises);
        const validParticipations = statuses.filter(Boolean) as ParticipantStatusType[];
        setParticipations(validParticipations);

        const activeParticipation = validParticipations.find(p =>
          p.current_stage !== 'evaluation_pending'
        );
        if (activeParticipation) {
          setSelectedCompetition(activeParticipation.event_id);
        }
      } else {
        setError(response.message || 'Error al cargar mis competencias');
      }
    } catch (err) {
      setError('Error al cargar mis competencias');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompetitionStatus = async (competitionId: number) => {
    if (!auth.token) return;

    try {
      // ✅ CORREGIDO: Eliminado auth.token como segundo parámetro
      const response = await competitionService.getParticipantStatus(competitionId);

      if (response.success && response.data) {
        setCompetitionStatus(response.data);
      }
    } catch (err) {
      console.error('Error loading competition status:', err);
    }
  };

  const handleImageUpload = async (file: File, imageTypeId: number) => {
    if (!auth.token || !competitionStatus) return;

    try {
      setIsUploadLoading(true);
      setUploadError(null);

      const uploadData: ImageUploadData = {
        participation_id: competitionStatus.participant_status.participation_id,
        image_type_id: imageTypeId,
        image: file
      };

      // ✅ CORREGIDO: Eliminado auth.token como segundo parámetro
      const response = await competitionService.uploadImage(uploadData);

      if (response.success) {
        await loadCompetitionStatus(selectedCompetition!);
      } else {
        setUploadError(response.message || 'Error al subir imagen');
      }
    } catch (err) {
      setUploadError('Error al subir imagen');
    } finally {
      setIsUploadLoading(false);
    }
  };

  const handleStatusAction = (action: string, data?: any) => {
    console.log('Action:', action, data);
  };

  const filterCompetitionsByTab = (comps: CompetitionDetailsType[]): CompetitionDetailsType[] => {
    const now = new Date();

    switch (activeTab) {
      case 'active':
        return comps.filter(c => {
          const startDate = new Date(c.competition.start_date);
          const endDate = new Date(c.competition.end_date);
          return startDate <= now && now <= endDate;
        });
      case 'completed':
        return comps.filter(c => new Date(c.competition.end_date) < now);
      default:
        return comps;
    }
  };

  const renderCompetitionsList = () => {
    const filteredCompetitions = filterCompetitionsByTab(competitions);

    return (
      <div className="competitions-list">
        <div className="tabs">
          <Button
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            Todas ({competitions.length})
          </Button>
          <Button
            className={`tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Activas ({filterCompetitionsByTab(competitions).length})
          </Button>
          <Button
            className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            Finalizadas
          </Button>
        </div>

        <div className="competition-items">
          {filteredCompetitions.map((competitionDetails) => (
            <div
              key={competitionDetails.competition.id}
              className={`competition-item ${selectedCompetition === competitionDetails.competition.id ? 'selected' : ''}`}
              onClick={() => setSelectedCompetition(competitionDetails.competition.id)}
            >
              <CompetitionCard
                competition={competitionDetails.competition}
                onViewDetails={() => navigate(`/competitions/${competitionDetails.competition.id}`, {
                  state: { competitionDetails }
                })}
                showActions={false}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderImageUploads = () => {
    if (!competitionStatus) return null;

    const { pending_image_types, submitted_images } = competitionStatus;

    return (
      <div className="image-uploads-section">
        <Card variant="flat">
          <div className="section-header">
            <h3>Imágenes Requeridas</h3>
            <p>Sube las imágenes necesarias para completar tu participación</p>
          </div>

          <div className="image-types-grid">
            {pending_image_types.map((imageType: CompetitionImageType) => {
              const submission = submitted_images.find(
                (sub: CompetitionSubmission) => sub.image_type_id === imageType.id
              );

              return (
                <ImageUpload
                  key={imageType.id}
                  imageType={imageType}
                  participationId={competitionStatus.participant_status.participation_id}
                  existingImage={submission ? {
                    file_name: submission.file_name,
                    uploaded_at: submission.uploaded_at
                  } : undefined}
                  onUpload={handleImageUpload}
                  isUploading={isUploadLoading}
                  error={uploadError}
                />
              );
            })}
          </div>
        </Card>
      </div>
    );
  };

  if (!auth.isAuthenticated) {
    return (
      <div className="auth-required">
        <Card variant="flat">
          <div className="auth-content">
            <h2>Acceso requerido</h2>
            <p>Debes iniciar sesión para ver tus competencias.</p>
            <Button
              className="btn btn-primary"
              onClick={() => navigate('/login')}
            >
              Iniciar sesión
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return <Loading message="Cargando mis competencias..." />;
  }

  if (error) {
    return (
      <div className="error-container">
        <ErrorMessage
          message={error}
          type="error"
        />
        <Button
          className="btn btn-primary"
          onClick={loadMyCompetitions}
        >
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="my-competitions-page">
      <div className="page-header">
        <h1>Mis Competencias</h1>
        <p>Gestiona tu participación en las competencias</p>

        <div className="header-actions">
          <Button
            className="btn btn-outline"
            onClick={() => navigate('/competitions')}
          >
            Explorar más competencias
          </Button>
        </div>
      </div>

      {participations.length === 0 ? (
        <Card className="empty-state" variant="flat">
          <div className="empty-content">
            <div className="empty-icon">🏆</div>
            <h3>No tienes competencias aún</h3>
            <p>
              Explora nuestras competencias disponibles y comienza a participar.
            </p>
            <Button
              className="btn btn-primary"
              onClick={() => navigate('/competitions')}
            >
              Ver competencias disponibles
            </Button>
          </div>
        </Card>
      ) : (
        <div className="my-competitions-content">
          <div className="competitions-sidebar">
            {renderCompetitionsList()}
          </div>

          <div className="competition-details-section">
            {selectedCompetition && competitionStatus ? (
              <>
                <ParticipantStatus
                  status={competitionStatus.participant_status}
                  nextActions={competitionStatus.next_actions}
                  submissions={competitionStatus.submitted_images}
                  onAction={handleStatusAction}
                />

                {competitionStatus.participant_status.terms_accepted &&
                  competitionStatus.participant_status.current_stage !== 'evaluation_pending' && (
                    renderImageUploads()
                  )}
              </>
            ) : (
              <Card className="no-selection" variant="flat">
                <div className="no-selection-content">
                  <p>Selecciona una competencia para ver los detalles</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCompetitions;